import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    
    // Automatically reload the page once if it's a chunk loading error (e.g. after a new deployment)
    const isChunkLoadError = 
      error?.name === 'ChunkLoadError' || 
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Unexpected token');

    if (isChunkLoadError) {
      const hasReloaded = sessionStorage.getItem('chunk_load_reloaded');
      
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_load_reloaded', 'true');
        console.warn('ChunkLoadError detected. Hard reloading the page...');
        window.location.reload();
      } else {
        console.error('Already attempted to reload for chunk error, but failed again.');
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-dark-bg p-4 text-center">
          <div className="bg-white dark:bg-dark-card p-8 rounded-xl shadow-lg max-w-md w-full border border-red-100 dark:border-red-900/30">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Something went wrong</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">
              The application encountered an unexpected error. This usually happens when the app has been updated in the background.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('chunk_load_reloaded');
                window.location.reload();
              }}
              className="w-full bg-light-primary dark:bg-dark-primary text-white py-2.5 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-all flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Page</span>
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
